import { useState } from 'react';
import { productTypes } from '@/mocks/orders';

interface NewOrderFormProps {
  onClose: () => void;
}

export default function NewOrderForm({ onClose }: NewOrderFormProps) {
  const [form, setForm] = useState({
    customerName: '',
    phone: '',
    city: '',
    district: '',
    address: '',
    productType: '',
    productName: '',
    quantity: 1,
    amount: 0,
    paymentType: 'kapida_odeme',
    orderSource: 'manuel',
    note: '',
    customerRequest: '',
    hasLogoFile: false,
  });

  const updateField = (field: string, value: string | number | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // In real app, this would save to database
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed right-0 top-0 h-full w-full max-w-[560px] bg-white z-50 shadow-2xl overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-stone-200 px-6 py-4 flex items-center justify-between z-10">
          <h3 className="text-lg font-bold text-stone-900">Yeni Sipariş Ekle</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-stone-100 text-stone-500 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Customer Info */}
          <div>
            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">Müşteri Bilgileri</h4>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-stone-500 block mb-1">Ad Soyad *</label>
                <input
                  type="text"
                  required
                  value={form.customerName}
                  onChange={(e) => updateField('customerName', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30"
                  placeholder="Müşteri adı soyadı"
                />
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Telefon *</label>
                <input
                  type="text"
                  required
                  value={form.phone}
                  onChange={(e) => updateField('phone', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30"
                  placeholder="05XX XXX XX XX"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-stone-500 block mb-1">İl *</label>
                  <input
                    type="text"
                    required
                    value={form.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30"
                    placeholder="İl"
                  />
                </div>
                <div>
                  <label className="text-xs text-stone-500 block mb-1">İlçe *</label>
                  <input
                    type="text"
                    required
                    value={form.district}
                    onChange={(e) => updateField('district', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30"
                    placeholder="İlçe"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Açık Adres *</label>
                <textarea
                  required
                  rows={2}
                  value={form.address}
                  onChange={(e) => updateField('address', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30 resize-none"
                  placeholder="Açık adres"
                />
              </div>
            </div>
          </div>

          {/* Product Info */}
          <div>
            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">Ürün Bilgileri</h4>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-stone-500 block mb-1">Ürün Tipi *</label>
                <select
                  required
                  value={form.productType}
                  onChange={(e) => updateField('productType', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30"
                >
                  <option value="">Seçiniz</option>
                  {productTypes.map((pt) => (
                    <option key={pt.value} value={pt.value}>{pt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Ürün Adı / Açıklama</label>
                <input
                  type="text"
                  value={form.productName}
                  onChange={(e) => updateField('productName', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30"
                  placeholder="Örn: 12 Adet MDF Araç Logosu"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-stone-500 block mb-1">Adet *</label>
                  <input
                    type="number"
                    required
                    min={1}
                    value={form.quantity}
                    onChange={(e) => updateField('quantity', parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30"
                  />
                </div>
                <div>
                  <label className="text-xs text-stone-500 block mb-1">Tutar (₺) *</label>
                  <input
                    type="number"
                    required
                    min={0}
                    value={form.amount}
                    onChange={(e) => updateField('amount', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-stone-500 block mb-1">Ödeme Tipi *</label>
                  <select
                    required
                    value={form.paymentType}
                    onChange={(e) => updateField('paymentType', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30"
                  >
                    <option value="kapida_odeme">Kapıda Ödeme</option>
                    <option value="havale">Havale / EFT</option>
                    <option value="kredi_karti">Kredi Kartı</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-stone-500 block mb-1">Sipariş Kaynağı</label>
                  <select
                    value={form.orderSource}
                    onChange={(e) => updateField('orderSource', e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30"
                  >
                    <option value="manuel">Manuel</option>
                    <option value="instagram_dm">Instagram DM</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="reklam">Reklam</option>
                    <option value="tavsiye">Tavsiye</option>
                  </select>
                </div>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.hasLogoFile}
                  onChange={(e) => updateField('hasLogoFile', e.target.checked)}
                  className="w-4 h-4 rounded border-stone-300 text-gold-500 focus:ring-gold-400"
                />
                <span className="text-xs text-stone-600">Logo / Görsel dosyası var</span>
              </label>
            </div>
          </div>

          {/* Notes */}
          <div>
            <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider mb-3">Notlar</h4>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-stone-500 block mb-1">Sipariş Notu</label>
                <textarea
                  rows={2}
                  value={form.note}
                  onChange={(e) => updateField('note', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30 resize-none"
                  placeholder="Sipariş ile ilgili notlar"
                />
              </div>
              <div>
                <label className="text-xs text-stone-500 block mb-1">Müşteri Özel İsteği</label>
                <textarea
                  rows={2}
                  value={form.customerRequest}
                  onChange={(e) => updateField('customerRequest', e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-stone-200 rounded-lg text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400/30 resize-none"
                  placeholder="Müşterinin özel istekleri"
                />
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              className="flex-1 px-4 py-2.5 bg-gold-500 text-white text-sm font-semibold rounded-lg hover:bg-gold-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              Siparişi Kaydet
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm text-stone-500 hover:text-stone-700 cursor-pointer whitespace-nowrap"
            >
              İptal
            </button>
          </div>
        </form>
      </div>
    </>
  );
}